import { FC, useEffect, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import { Label } from '@/components/Forms';
import InfoTooltip from '@/components/InfoTooltip';
import { BankResource } from '@/types/resources';
import { api } from '@/utils/api';

import { InvoicesBankSelect } from './InvoicesBankSelect';

interface Props {
  control: Control<any>;
  name: string;
  showNewBankDetails?: boolean;
  setValue?: UseFormSetValue<any>;
  initialBankId?: number | null;
  initialReceiver?: string;
  label?: string;
  receiverFieldName?: string;
  onBankSelect?: (bank: BankResource | null) => void;
  disableWhenSingleBank?: boolean;
  autoSelectSingleBank?: boolean;
}

const InvoicesBankField: FC<Props> = ({
  control,
  name,
  setValue,
  initialBankId,
  initialReceiver,
  label = 'Receiver Bank',
  receiverFieldName = 'receiver',
  showNewBankDetails = true,
  onBankSelect,
  disableWhenSingleBank = false,
  autoSelectSingleBank = false,
}) => {
  const [banks, setBanks] = useState<{ label: string; value: number | null }[]>([]);
  const [bankResources, setBankResources] = useState<BankResource[]>([]);

  const receiver = useWatch({ control, name: receiverFieldName });
  const selectedBankId = useWatch({ control, name });

  useEffect(() => {
    if (!receiver) {
      setBanks([]);
      setBankResources([]);
      return;
    }

    const receiverChangedFromInitial = receiver !== initialReceiver;

    api
      .get<never, BankResource[]>(`/v1/payments/members/banks/${receiver}`)
      .then((suggestions) => {
        const bankVariations = suggestions.map((suggestion) => ({
          label: `${suggestion.bank ?? ''} · ${suggestion.iban.substring(suggestion.iban.length - 4)}`,
          value: suggestion.id,
        }));

        const finalBanks = [
          ...bankVariations,
          ...(showNewBankDetails ? [{ label: 'New bank details', value: null }] : []),
        ];

        setBanks(finalBanks);
        setBankResources(suggestions);

        if (setValue) {
          if (autoSelectSingleBank && suggestions.length === 1) {
            setValue(name, suggestions[0].id);
          } else if (receiverChangedFromInitial) {
            setValue(name, undefined);
          }
        }
      });
  }, [
    receiver,
    setValue,
    name,
    initialBankId,
    initialReceiver,
    showNewBankDetails,
    autoSelectSingleBank,
  ]);

  useEffect(() => {
    if (!onBankSelect) return;
    if (selectedBankId === null || selectedBankId === undefined) {
      onBankSelect(null);
      return;
    }
    const found = bankResources.find((b) => b.id === selectedBankId) ?? null;
    onBankSelect(found);
  }, [selectedBankId, bankResources, onBankSelect]);

  return (
    <>
      <Label htmlFor={name}>
        {label}
        <InfoTooltip
          width={270}
          title={label}
          content="Select the receiver's bank that has been added to the invoice. If the user's information doesn't exist in the system and suggestions are not appearing in the dropdown selection field, choose 'New bank details'."
          position="bottom-center"
        />
      </Label>
      <InvoicesBankSelect name={name} control={control} options={banks || []} disabled={disableWhenSingleBank && bankResources.length === 1} />
    </>
  );
};

export default InvoicesBankField;
